import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  Self,
  SimpleChanges,
} from '@angular/core';
import {
  CanUpdateErrorState,
  ErrorStateMatcher,
  MAT_INPUT_VALUE_ACCESSOR,
  MatAutocompleteSelectedEvent,
  MatFormFieldControl,
  MatInputBase,
  mixinErrorState,
} from '@angular/material';
import {ControlValueAccessor, FormGroupDirective, NgControl, NgForm} from '@angular/forms';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {Platform} from '@angular/cdk/platform';
import {Subject} from 'rxjs/Subject';
import {JobTitleService} from './job-title.service';
import {FocusMonitor} from '@angular/cdk/a11y';
import {JobTitle} from '../../@models/job-title.model';
import * as _ from 'lodash';


export const _JobTitleMixin = mixinErrorState(MatInputBase);


@Component({
  selector: 'bb-job-title-field',
  templateUrl: './job-title-field.component.html',
  styleUrls: ['./job-title-field.component.scss'],
  providers: [
    JobTitleService,
    {
      provide: MatFormFieldControl,
      useExisting: JobTitleFieldComponent,
    },
  ],
})
export class JobTitleFieldComponent extends _JobTitleMixin implements MatFormFieldControl<string>, OnChanges,
  OnDestroy, DoCheck, CanUpdateErrorState, ControlValueAccessor {

  errorState: boolean;
  protected _disabled = false;
  _ariaDescribedby: string;
  protected _id: string;
  protected _uid = _.uniqueId('job-title-');
  controlType = 'job-title-';
  focused = false;
  stateChanges: Subject<void> = new Subject<void>();
  @Input() placeholder = '';
  private _readonly = false;
  protected _required = false;
  /** An object used to control when error messages are shown. */
  @Input() errorStateMatcher: ErrorStateMatcher;
  _onChange: (value: string) => void = _.noop;
  _value: string;

  //

  @Output() setJobTitlePk = new EventEmitter();

  //

  writeValue(value: string): void {
    this._value = value;
  }

  /**
   * Implemented as part of MatFormFieldControl.
   * @docs-private
   */
  @Input()
  get disabled(): boolean {
    if (this.ngControl && this.ngControl.disabled !== null) {
      return this.ngControl.disabled;
    }
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);

    // Browsers may not fire the blur event if the input is disabled too quickly.
    // Reset from here to ensure that the element doesn't become stuck.
    if (this.focused) {
      this.focused = false;
      this.stateChanges.next();
    }
  }

  /**
   * Implemented as part of MatFormFieldControl.
   * @docs-private
   */
  @Input()
  get id(): string {
    return this._id;
  }

  set id(value: string) {
    this._id = value || this._uid;
  }

  /**
   * Implemented as part of MatFormFieldControl.
   * @docs-private
   */
  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
  }

  /**
   * Implemented as part of MatFormFieldControl.
   * @docs-private
   */
  @Input()
  get value(): string {
    return this._value;
  }

  set value(value: string) {
    if (value !== this._value) {
      this.writeValue(value);
      this.stateChanges.next();
      this._value = value;
      this._onChange(value);
    }
  }

  /** Whether the element is readonly. */
  @Input()
  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = coerceBooleanProperty(value);
  }

  /**
   * Implemented as part of MatFormFieldControl.
   * @docs-private
   */
  get empty(): boolean {
    return !this._value || this._value.length === 0;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  //

  setDisabledState?(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  setDescribedByIds(ids: string[]): void {
    this._ariaDescribedby = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.stateChanges.next();
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  ngDoCheck(): void {
    // Copied from angualr 2 matInput
    if (this.ngControl) {
      // noinspection TypeScriptUnresolvedFunction
      this.updateErrorState();
    }
  }

  //

  updateSearch() {
    this.service.jobTitle.next(this._value);
  }

  selectedJobTitle(event: MatAutocompleteSelectedEvent) {
    const jobPk = +event.option._getHostElement().dataset['jobTitlePk'];
    this.service.selectedJobTitlePk.next(jobPk);
  }

  //

  constructor(protected _elementRef: ElementRef,
              protected _platform: Platform,
              /** @docs-private */
              @Optional() @Self() public ngControl: NgControl,
              @Optional() _parentForm: NgForm,
              @Optional() _parentFormGroup: FormGroupDirective,
              _defaultErrorStateMatcher: ErrorStateMatcher,
              @Optional() @Self() @Inject(MAT_INPUT_VALUE_ACCESSOR) inputValueAccessor: any,
              private fm: FocusMonitor,
              public service: JobTitleService) {

    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }

    this.fm.monitor(this._elementRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });

    this.service.finalJobTitle.subscribe((job: JobTitle.Model) => {
      if (job) {
        this.setJobTitlePk.emit(job.pk);
      } else {
        this.setJobTitlePk.emit(null);
      }
    });
  }
}
